<script setup lang="ts">
import { useSelectedSiteStore } from '@/sites/stores/selectedSiteStore';
import { Assert } from 'shared/assert';
import { computed, onMounted, ref, watch } from 'vue';
import { useSaveSettings } from '@/settings/api/saveSettings';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useAuthStore } from '@/auth/store/authStore';
import { Button, Select, InputGroup, InputGroupAddon, InputText } from 'primevue';
import type { MapSettings } from 'shared/setting/domain/setting';
import { useSettingStore } from 'shared/setting/stores/settingStore';
import Dialog from 'primevue/dialog';

export interface MapSettingOptions extends Omit<MapSettings, 'minZoom'> {
  id: string,
  name: string,
  localStorage: boolean,
  siteId: number,
  basePath: string
}

const selectedSiteStore = useSelectedSiteStore();
const saveSettings = useSaveSettings();
const settings = useSettingStore();
const authStore = useAuthStore();
const actionFailedAlert = useActionFailedAlert();
const defaultMapSetting = ref<MapSettingOptions>();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const visible = ref(false);

const localStorageKey = 'map-setting-options';
const selectedMapViewKey = 'selected-map-view';

const props = defineProps<{
  mapApi: MapApi,
}>();

export interface MapApi {
  get bearing(): number;
  get zoom(): number;
  get center(): { longitude: number, latitude: number };
  flyTo(options: { center: { longitude: number, latitude: number }, bearing: number, zoom: number }): void;
}

const saveMapModeLoading = ref<boolean>(false);
const saveMapModeDisabled = computed<boolean>(() => {
  if (selectedSiteStore.siteId === undefined) return true;
  if (saveMapModeLoading.value) return true;
  if (selectedMapView.value == defaultMapSetting.value && !authStore.isAdmin) return true;

  return false;
});

const mapSettingOptions = ref<MapSettingOptions[]>([]);
const selectedMapView = ref<MapSettingOptions>();

const inputName = ref();
const disableDeleteBtn = computed<boolean>(() => {
  if (selectedMapView.value == undefined) return true;
  if (selectedMapView.value.localStorage) return false;
  return true;
});

const basePath = document.querySelector('base')?.href || '/';

async function onSaveMapModeClick() {
  if (selectedMapView.value == undefined) return;
  const siteId = selectedSiteStore.siteId;
  Assert.definied(siteId);
  const mapApi = props.mapApi;

  const { bearing, zoom, center } = mapApi;

  const newMapSetting: MapSettingOptions = {
    id: selectedMapView.value.id,
    name: selectedMapView.value.name,
    localStorage: selectedMapView.value.localStorage,
    siteId: siteId,
    latitude: center.latitude,
    longitude: center.longitude,
    bearing: bearing,
    pitch: 0,
    zoom: zoom,
    basePath: basePath
  }

  const index = mapSettingOptions.value.findIndex(option => option.id == selectedMapView.value?.id);

  if (selectedMapView.value.localStorage) {
    const currentLocalStorageString = localStorage.getItem(localStorageKey);
    if (currentLocalStorageString) {

      const currentLocalStorage: MapSettingOptions[] = JSON.parse(currentLocalStorageString);
      const localStorageIndex = currentLocalStorage.findIndex(option => option.id == selectedMapView.value?.id);

      currentLocalStorage[localStorageIndex] = newMapSetting;
      localStorage.setItem(localStorageKey, JSON.stringify(currentLocalStorage));

      mapSettingOptions.value[index] = newMapSetting;
      selectedMapView.value = newMapSetting;
    }
  } else {
    try {
      saveMapModeLoading.value = true;

      const response = await saveSettings.execute({
        sharedClient: {
          map: {
            latitude: center.latitude,
            longitude: center.longitude,
            bearing: bearing,
            pitch: 0,
            zoom: zoom,
            minZoom: 13,
          }
        }
      });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
      }

    } finally {
      saveMapModeLoading.value = false;
      mapSettingOptions.value[index] = newMapSetting;
      selectedMapView.value = newMapSetting;

    }
  }
}

onMounted(() => {
  updateOptions();
});


watch(() => selectedSiteStore.siteId, updateOptions)

watch(() => selectedMapView.value, () => {
  localStorage.setItem(selectedMapViewKey, JSON.stringify(selectedMapView.value))
});

function updateOptions() {

  if (!selectedSiteStore.siteId) return;
  mapSettingOptions.value = [];

  defaultMapSetting.value = {
    id: 'default-id',
    name: translations.value.generic.default,
    localStorage: false,
    siteId: selectedSiteStore.siteId,
    longitude: settings.mapSettings.longitude,
    latitude: settings.mapSettings.latitude,
    zoom: settings.mapSettings.zoom,
    bearing: settings.mapSettings.bearing,
    pitch: settings.mapSettings.pitch,
    basePath: basePath
  }
  mapSettingOptions.value.push(defaultMapSetting.value);

  const currentLocalStorageString = localStorage.getItem(localStorageKey);

  if (currentLocalStorageString) {

    let currentLocalStorage: MapSettingOptions[] = [];
    try {
      currentLocalStorage = JSON.parse(currentLocalStorageString);
    } catch {
      localStorage.removeItem(localStorageKey);
    }

    currentLocalStorage.forEach(option => {
      if (option.siteId == selectedSiteStore.siteId && (option.basePath == basePath || option.basePath == undefined)) {
        mapSettingOptions.value.push(option);
      }
    });
  }
  if (mapSettingOptions.value.length > 0) {

    const storedSelectedMapViewString = localStorage.getItem(selectedMapViewKey);

    if (storedSelectedMapViewString) {

      let storedSelectedMapView: MapSettingOptions | null = null;

      try {
        storedSelectedMapView = JSON.parse(storedSelectedMapViewString) as MapSettingOptions;
      } catch {
        localStorage.removeItem(selectedMapViewKey);
      }

      if (storedSelectedMapView) {
        const index = mapSettingOptions.value.findIndex(option => option.id == storedSelectedMapView.id)
        selectedMapView.value = mapSettingOptions.value[index];

      } else {
        selectedMapView.value = mapSettingOptions.value[0];
      }
    } else {
      selectedMapView.value = mapSettingOptions.value[0];
    }
  }
}

function onAddClick() {
  visible.value = true;
}

function onDeleteClick() {

  const currentLocalStorageString = localStorage.getItem(localStorageKey);
  const currentLocalStorage: MapSettingOptions[] = currentLocalStorageString ? JSON.parse(currentLocalStorageString) : [];

  const index = mapSettingOptions.value.findIndex(option => option.id == selectedMapView.value?.id)
  if (index) {
    mapSettingOptions.value.splice(index, 1);
  }

  const localStorageIndex = currentLocalStorage.findIndex(option => option.id == selectedMapView.value?.id)
  if (localStorageIndex) {
    currentLocalStorage.splice(localStorageIndex, 1);
  }

  localStorage.setItem(localStorageKey, JSON.stringify(currentLocalStorage));
  localStorage.removeItem(selectedMapViewKey);
}

function addMapSettingOption() {
  const mapApi = props.mapApi;
  const siteId = selectedSiteStore.siteId;
  Assert.definied(siteId);
  visible.value = false;

  const { bearing, zoom, center } = mapApi;

  const newMapSetting: MapSettingOptions = {
    id: crypto.randomUUID(),
    name: inputName.value,
    localStorage: true,
    siteId: siteId,
    latitude: center.latitude,
    longitude: center.longitude,
    bearing: bearing,
    pitch: 0,
    zoom: zoom,
    basePath: basePath
  }
  mapSettingOptions.value.push(newMapSetting);
  selectedMapView.value = newMapSetting;

  const currentLocalStorageString = localStorage.getItem(localStorageKey);
  const currentLocalStorage: MapSettingOptions[] = currentLocalStorageString ? JSON.parse(currentLocalStorageString) : [];
  currentLocalStorage.push(newMapSetting);

  const mapSettingJSON = JSON.stringify(currentLocalStorage);
  localStorage.setItem(localStorageKey, mapSettingJSON)

}

function updateMapSettings() {
  const mapApi = props.mapApi;
  if (!selectedMapView.value) return;

  mapApi.flyTo({
    center: selectedMapView.value,
    zoom: selectedMapView.value.zoom,
    bearing: selectedMapView.value.bearing
  });
}

</script>

<template>
  <Dialog v-model:visible="visible" modal :header="translations.dialogs.mapView.title" :style="{ width: '25rem' }">
    <label for="inputNameField">{{ translations.dialogs.mapView.enterName }}</label>
    <InputText id="inputNameField" v-model="inputName" fluid></InputText>
    <div class="flex justify-end gap-2 pt-3">
      <Button type="button" :label="translations.generic.save" @click="addMapSettingOption"></Button>
      <Button type="button" :label="translations.generic.abort" severity="secondary" @click="visible = false"></Button>
    </div>
  </Dialog>
  <div class="w-48">
    <InputGroup>
      <InputGroupAddon>
        <i class="pi pi-expand"></i>
      </InputGroupAddon>
      <Select v-model="selectedMapView" :options="mapSettingOptions" optionLabel="name" placeholder=""
        @change="updateMapSettings">
        <template #footer>
          <div class="grid grid-cols-3 p-3">
            <Button :label="translations.generic.save" fluid severity="primary" variant="text" size="small"
              icon="pi pi-save" :disabled="saveMapModeDisabled" @click="onSaveMapModeClick" />
            <Button :label="translations.generic.add" fluid severity="secondary" variant="text" size="small"
              icon="pi pi-plus" @click="onAddClick" />
            <Button :label="translations.generic.delete" fluid severity="danger" variant="text" size="small"
              icon="pi pi-trash" @click="onDeleteClick" :disabled="disableDeleteBtn" />
          </div>
        </template>
      </Select>
    </InputGroup>
  </div>
</template>
